import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import camelCase from "lodash/camelCase";

import CalculatorContainer from "./CalculatorContainer";
import keypads from "./constants/keypads";

import Decimal from "./utils/decimal-custom";

const Calculator = ({ mode: initialMode = "scientific" }) => {
  // Display value is the current value shown on the display screen
  const [displayValue, setDisplayValue] = useState("0");
  // Current output is the stored value that will used as the first operand
  // in binary operations. When chaining multiple operations together, this
  // will be the result of the previous operation. On the first operation,
  // the displayValue when the operation key is pressed is stored as the
  // current output
  const [currentOutput, setCurrentOutput] = useState(null);
  // The active operation. For binary operations, this is set when the
  // operation key is pressed. The user then enters the second input value
  const [currentOperation, setCurrentOperation] = useState(null);
  // Determines what to do when a number key is pressed. If this is false,
  // the number will be added to the current display value. When set to true,
  // the next number key press will reset display value and start a new
  // input. This is the case after pressing C, or after pressing an operation
  // key.
  const [resetDisplayValueOnNextKeyPress, setResetDisplayValueOnNextKeyPress] = useState(true);
  // Sets the mode: 'basic' or 'scientific' (currently only 'basic').
  // This will determine which keypad to use, as well as other mode-specific
  // behavior and styles.
  const [mode, setMode] = useState(initialMode || "scientific");
  // Unit of measurement for angles
  const [trigUnit, setTrigUnit] = useState("deg");
  // The stored memory value.
  const [memory, setMemory] = useState(null);
  // The last key pressed.
  const [lastKeyPressed, setLastKeyPressed] = useState("");

  // Set mode based on device orientation (only on small screens)
  useEffect(() => {
    const mobilePortrait = window.matchMedia("(max-width: 450px)");
    const mobileLandscape = window.matchMedia(
      "(max-width: 740px) and (max-height: 450px)"
    );
    const landscape = window.matchMedia("(orientation: landscape)");

    const setModeByOrientation = (media) => {
      if (!(mobilePortrait.matches || mobileLandscape.matches)) return;
      setMode(media.matches ? "scientific" : "basic");
    };

    landscape.addListener(setModeByOrientation);
    setModeByOrientation(landscape);

    return () => {
      landscape.removeListener(setModeByOrientation);
    };
  }, []);

  const changeMode = (newMode) => {
    setMode(newMode);
  };

  /**
   * Handle Number Key
   * Also handles the decimal key
   */
  const handleNumberKey = (key) => {
    const number = key.textContent;
    let newDisplayValue = resetDisplayValueOnNextKeyPress ? "" : displayValue;

    // Handle decimal key...
    // if display value already contains a decimal, the key has no effect
    if (key.id === "decimal" && !newDisplayValue.includes(".")) {
      // A leading 0 is shown when the decimal is added to 0
      newDisplayValue = newDisplayValue ? newDisplayValue + "." : "0.";
    } else if (key.id !== "decimal") {
      newDisplayValue += number;
    }

    setDisplayValue(newDisplayValue.replace(/^0+(?!\.)/, "0"));
    setResetDisplayValueOnNextKeyPress(false);
  };

  const handleConstantKey = ({ id: constant }) => {
    setDisplayValue(Decimal[constant].toString());
    setResetDisplayValueOnNextKeyPress(true);
  };

  /**
   * Handle equals key
   * @todo support pressing equals multiple times to repeat the previous
   * operation.
   */
  const handleEqualsKey = () => {
    if (!currentOperation) {
      return;
    }

    const firstOperand = new Decimal(currentOutput);
    const secondOperand = displayValue;
    const output = firstOperand[currentOperation](secondOperand);

    setCurrentOperation(null);
    setCurrentOutput(output);
    setDisplayValue(output.toString());
    setResetDisplayValueOnNextKeyPress(true);
  };

  /**
   * Handle Binary Operations (operations with two input values)
   */
  const handleBinaryOperationKey = (key) => {
    const operation = key.id;

    if (currentOperation && lastKeyPressed.type === "number") {
      handleEqualsKey();
    }

    setCurrentOperation(operation);
    setCurrentOutput(displayValue);
    setResetDisplayValueOnNextKeyPress(true);
  };

  /**
   * Handle unary operations (operations with one input value).
   * Unary operations are performed directly on the display value. The
   * current display value is used as the input (operand), and the result of
   * the operation (output) becomes the new display value.
   */
  const handleUnaryOperationKey = (key) => {
    const operation = key.id;
    let operand = new Decimal(displayValue);

    if (/^(sin|cos|tan)$/.test(operation) && trigUnit === "deg") {
      const PI = Decimal.acos(-1);
      operand = operand.times(new Decimal(PI).dividedBy(180));
    }

    const output = operand[operation]();

    setDisplayValue(output.toString());
    setResetDisplayValueOnNextKeyPress(true);
  };

  /**
   * Handle Clear key
   * This resets the app to initial state.
   */
  const handleClearKey = () => {
    setCurrentOperation(null);
    setCurrentOutput(null);
    setDisplayValue("0");
    setResetDisplayValueOnNextKeyPress(true);
  };

  const handleFunctionKey = ({ id: functionName }) => {
    switch (functionName) {
      case "trigUnit":
        return setTrigUnit(prevUnit => (prevUnit === "deg" ? "rad" : "deg"));
      case "memoryAdd":
        return setMemory(currentMemory =>
          (currentMemory ? currentMemory.plus(displayValue) : new Decimal(displayValue)));
      case "memorySubtract":
        return setMemory(currentMemory =>
          (currentMemory ? currentMemory.minus(displayValue) : null));
      case "memoryClear":
        return setMemory(null);
      case "memoryRecall":
        return (
          memory
            && (setDisplayValue(memory.toString()), setResetDisplayValueOnNextKeyPress(true))
        );
      case "random":
        return (
          setDisplayValue(new Decimal(Math.random()).toString()),
          setResetDisplayValueOnNextKeyPress(true)
        );
      default:
        return null;
    }
  };

  /**
   * Handles click event for all calculator keys
   */
  const handleClick = (event, { type }) => {
    setLastKeyPressed({ type, id: event.currentTarget.id });
    const handlers = {
      handleNumberKey,
      handleConstantKey,
      handleBinaryOperationKey,
      handleUnaryOperationKey,
      handleEqualsKey,
      handleClearKey,
      handleFunctionKey
    };

    const handlerName = camelCase(`handle-${type}-Key`);
    if (handlers[handlerName]) {
      handlers[handlerName](event.currentTarget);
    }
  };

  return (
    <CalculatorContainer
      keys={keypads[mode]}
      currentOperation={currentOperation}
      mode={mode}
      changeMode={changeMode}
      displayValue={displayValue}
      trigUnit={trigUnit}
      memory={memory}
      handleClick={handleClick}
    />
  );
};

Calculator.propTypes = {
  mode: PropTypes.oneOf(["basic", "scientific", "fred"]),
};

export default Calculator;
